import { createContext, useContext, useEffect, useState, useCallback } from 'react';

export interface ThemeColors {
    background: string;
    foreground: string;
    card: string;
    'card-foreground': string;
    popover: string;
    'popover-foreground': string;
    primary: string;
    'primary-foreground': string;
    secondary: string;
    'secondary-foreground': string;
    muted: string;
    'muted-foreground': string;
    accent: string;
    'accent-foreground': string;
    destructive: string;
    'destructive-foreground': string;
    border: string;
    input: string;
    ring: string;
    sidebar: string;
    'sidebar-foreground': string;
    'sidebar-accent': string;
    'sidebar-accent-foreground': string;
    'sidebar-border': string;
    'sidebar-primary': string;
    'sidebar-primary-foreground': string;
}

export interface Theme {
    name: string;
    label: string;
    colors: ThemeColors;
    dark: ThemeColors;
    swatch: string; // Preview color for the picker
}

export const themes: Theme[] = [
    {
        name: 'slate',
        label: 'Slate',
        swatch: '#0f172a',
        colors: {
            background: '0 0% 100%',
            foreground: '240 10% 3.9%',
            card: '0 0% 100%',
            'card-foreground': '240 10% 3.9%',
            popover: '0 0% 100%',
            'popover-foreground': '240 10% 3.9%',
            primary: '240 5.9% 10%',
            'primary-foreground': '0 0% 98%',
            secondary: '240 4.8% 95.9%',
            'secondary-foreground': '240 5.9% 10%',
            muted: '240 4.8% 95.9%',
            'muted-foreground': '240 3.8% 46.1%',
            accent: '240 4.8% 95.9%',
            'accent-foreground': '240 5.9% 10%',
            destructive: '0 84.2% 60.2%',
            'destructive-foreground': '0 0% 98%',
            border: '240 5.9% 90%',
            input: '240 5.9% 90%',
            ring: '240 5.9% 10%',
            sidebar: '0 0% 98%',
            'sidebar-foreground': '240 5.3% 26.1%',
            'sidebar-accent': '240 4.8% 95.9%',
            'sidebar-accent-foreground': '240 5.9% 10%',
            'sidebar-border': '220 13% 91%',
            'sidebar-primary': '240 5.9% 10%',
            'sidebar-primary-foreground': '0 0% 98%',
        },
        dark: {
            background: '240 10% 3.9%',
            foreground: '0 0% 98%',
            card: '240 10% 3.9%',
            'card-foreground': '0 0% 98%',
            popover: '240 10% 3.9%',
            'popover-foreground': '0 0% 98%',
            primary: '0 0% 98%',
            'primary-foreground': '240 5.9% 10%',
            secondary: '240 3.7% 15.9%',
            'secondary-foreground': '0 0% 98%',
            muted: '240 3.7% 15.9%',
            'muted-foreground': '240 5% 64.9%',
            accent: '240 3.7% 15.9%',
            'accent-foreground': '0 0% 98%',
            destructive: '0 62.8% 30.6%',
            'destructive-foreground': '0 0% 98%',
            border: '240 4% 20%',
            input: '240 4% 20%',
            ring: '240 5% 40%',
            sidebar: '240 5.9% 10%',
            'sidebar-foreground': '240 4.8% 95.9%',
            'sidebar-accent': '240 3.7% 15.9%',
            'sidebar-accent-foreground': '240 4.8% 95.9%',
            'sidebar-border': '240 4% 18%',
            'sidebar-primary': '0 0% 98%',
            'sidebar-primary-foreground': '240 5.9% 10%',
        },
    },
    {
        name: 'blue',
        label: 'Blue',
        swatch: '#2563eb',
        colors: {
            background: '214 50% 99%',
            foreground: '222 47% 11%',
            card: '210 50% 99.5%',
            'card-foreground': '222 47% 11%',
            popover: '0 0% 100%',
            'popover-foreground': '222 47% 11%',
            primary: '221 83% 53%',
            'primary-foreground': '210 40% 98%',
            secondary: '214 45% 95%',
            'secondary-foreground': '222 47% 11%',
            muted: '214 45% 95%',
            'muted-foreground': '215 16% 47%',
            accent: '214 50% 93%',
            'accent-foreground': '222 47% 11%',
            destructive: '0 84% 60%',
            'destructive-foreground': '210 40% 98%',
            border: '214 35% 88%',
            input: '214 35% 88%',
            ring: '221 83% 53%',
            sidebar: '214 45% 97%',
            'sidebar-foreground': '222 47% 25%',
            'sidebar-accent': '214 50% 93%',
            'sidebar-accent-foreground': '222 47% 11%',
            'sidebar-border': '214 35% 88%',
            'sidebar-primary': '221 83% 53%',
            'sidebar-primary-foreground': '210 40% 98%',
        },
        dark: {
            background: '222 47% 6%',
            foreground: '210 40% 98%',
            card: '222 47% 6%',
            'card-foreground': '210 40% 98%',
            popover: '222 47% 6%',
            'popover-foreground': '210 40% 98%',
            primary: '217 91% 60%',
            'primary-foreground': '222 47% 6%',
            secondary: '217 33% 17%',
            'secondary-foreground': '210 40% 98%',
            muted: '217 33% 17%',
            'muted-foreground': '215 20% 65%',
            accent: '217 33% 17%',
            'accent-foreground': '210 40% 98%',
            destructive: '0 63% 31%',
            'destructive-foreground': '210 40% 98%',
            border: '217 20% 22%',
            input: '217 20% 22%',
            ring: '224 50% 40%',
            sidebar: '222 47% 8%',
            'sidebar-foreground': '210 40% 90%',
            'sidebar-accent': '217 33% 15%',
            'sidebar-accent-foreground': '210 40% 98%',
            'sidebar-border': '217 20% 20%',
            'sidebar-primary': '217 91% 60%',
            'sidebar-primary-foreground': '222 47% 6%',
        },
    },
    {
        name: 'green',
        label: 'Green',
        swatch: '#16a34a',
        colors: {
            background: '140 30% 99%',
            foreground: '150 40% 6%',
            card: '140 35% 99.5%',
            'card-foreground': '150 40% 6%',
            popover: '0 0% 100%',
            'popover-foreground': '150 40% 6%',
            primary: '142 71% 45%',
            'primary-foreground': '144 80% 98%',
            secondary: '140 35% 95%',
            'secondary-foreground': '150 40% 10%',
            muted: '140 35% 95%',
            'muted-foreground': '150 10% 45%',
            accent: '140 40% 92%',
            'accent-foreground': '150 40% 10%',
            destructive: '0 84% 60%',
            'destructive-foreground': '0 0% 98%',
            border: '140 25% 87%',
            input: '140 25% 87%',
            ring: '142 71% 45%',
            sidebar: '140 35% 96.5%',
            'sidebar-foreground': '150 40% 20%',
            'sidebar-accent': '140 40% 92%',
            'sidebar-accent-foreground': '150 40% 10%',
            'sidebar-border': '140 25% 87%',
            'sidebar-primary': '142 71% 45%',
            'sidebar-primary-foreground': '144 80% 98%',
        },
        dark: {
            background: '150 40% 4%',
            foreground: '140 30% 96%',
            card: '150 40% 4%',
            'card-foreground': '140 30% 96%',
            popover: '150 40% 4%',
            'popover-foreground': '140 30% 96%',
            primary: '142 71% 45%',
            'primary-foreground': '150 40% 4%',
            secondary: '150 20% 14%',
            'secondary-foreground': '140 30% 96%',
            muted: '150 20% 14%',
            'muted-foreground': '150 10% 55%',
            accent: '150 20% 14%',
            'accent-foreground': '140 30% 96%',
            destructive: '0 63% 31%',
            'destructive-foreground': '0 0% 98%',
            border: '150 15% 20%',
            input: '150 15% 20%',
            ring: '142 50% 35%',
            sidebar: '150 40% 6%',
            'sidebar-foreground': '140 30% 90%',
            'sidebar-accent': '150 20% 12%',
            'sidebar-accent-foreground': '140 30% 96%',
            'sidebar-border': '150 15% 18%',
            'sidebar-primary': '142 71% 45%',
            'sidebar-primary-foreground': '150 40% 4%',
        },
    },
    {
        name: 'violet',
        label: 'Violet',
        swatch: '#7c3aed',
        colors: {
            background: '270 30% 99%',
            foreground: '270 50% 8%',
            card: '270 35% 99.5%',
            'card-foreground': '270 50% 8%',
            popover: '0 0% 100%',
            'popover-foreground': '270 50% 8%',
            primary: '263 70% 50%',
            'primary-foreground': '270 50% 98%',
            secondary: '270 35% 95%',
            'secondary-foreground': '270 50% 10%',
            muted: '270 35% 95%',
            'muted-foreground': '270 10% 47%',
            accent: '270 40% 93%',
            'accent-foreground': '270 50% 10%',
            destructive: '0 84% 60%',
            'destructive-foreground': '0 0% 98%',
            border: '270 25% 88%',
            input: '270 25% 88%',
            ring: '263 70% 50%',
            sidebar: '270 35% 97%',
            'sidebar-foreground': '270 50% 22%',
            'sidebar-accent': '270 40% 93%',
            'sidebar-accent-foreground': '270 50% 10%',
            'sidebar-border': '270 25% 88%',
            'sidebar-primary': '263 70% 50%',
            'sidebar-primary-foreground': '270 50% 98%',
        },
        dark: {
            background: '270 50% 4%',
            foreground: '270 30% 96%',
            card: '270 50% 4%',
            'card-foreground': '270 30% 96%',
            popover: '270 50% 4%',
            'popover-foreground': '270 30% 96%',
            primary: '263 70% 58%',
            'primary-foreground': '270 50% 4%',
            secondary: '270 20% 14%',
            'secondary-foreground': '270 30% 96%',
            muted: '270 20% 14%',
            'muted-foreground': '270 10% 55%',
            accent: '270 20% 14%',
            'accent-foreground': '270 30% 96%',
            destructive: '0 63% 31%',
            'destructive-foreground': '0 0% 98%',
            border: '270 15% 20%',
            input: '270 15% 20%',
            ring: '263 50% 45%',
            sidebar: '270 50% 6%',
            'sidebar-foreground': '270 30% 90%',
            'sidebar-accent': '270 20% 12%',
            'sidebar-accent-foreground': '270 30% 96%',
            'sidebar-border': '270 15% 18%',
            'sidebar-primary': '263 70% 58%',
            'sidebar-primary-foreground': '270 50% 4%',
        },
    },
    {
        name: 'rose',
        label: 'Rose',
        swatch: '#e11d48',
        colors: {
            background: '350 30% 99%',
            foreground: '350 40% 8%',
            card: '350 35% 99.5%',
            'card-foreground': '350 40% 8%',
            popover: '0 0% 100%',
            'popover-foreground': '350 40% 8%',
            primary: '347 77% 50%',
            'primary-foreground': '350 40% 98%',
            secondary: '350 35% 95%',
            'secondary-foreground': '350 40% 10%',
            muted: '350 35% 95%',
            'muted-foreground': '350 10% 47%',
            accent: '350 40% 93%',
            'accent-foreground': '350 40% 10%',
            destructive: '0 84% 60%',
            'destructive-foreground': '0 0% 98%',
            border: '350 25% 88%',
            input: '350 25% 88%',
            ring: '347 77% 50%',
            sidebar: '350 35% 97%',
            'sidebar-foreground': '350 40% 22%',
            'sidebar-accent': '350 40% 93%',
            'sidebar-accent-foreground': '350 40% 10%',
            'sidebar-border': '350 25% 88%',
            'sidebar-primary': '347 77% 50%',
            'sidebar-primary-foreground': '350 40% 98%',
        },
        dark: {
            background: '350 40% 4%',
            foreground: '350 30% 96%',
            card: '350 40% 4%',
            'card-foreground': '350 30% 96%',
            popover: '350 40% 4%',
            'popover-foreground': '350 30% 96%',
            primary: '347 77% 50%',
            'primary-foreground': '350 40% 98%',
            secondary: '350 20% 14%',
            'secondary-foreground': '350 30% 96%',
            muted: '350 20% 14%',
            'muted-foreground': '350 10% 55%',
            accent: '350 20% 14%',
            'accent-foreground': '350 30% 96%',
            destructive: '0 63% 31%',
            'destructive-foreground': '0 0% 98%',
            border: '350 15% 20%',
            input: '350 15% 20%',
            ring: '347 50% 40%',
            sidebar: '350 40% 6%',
            'sidebar-foreground': '350 30% 90%',
            'sidebar-accent': '350 20% 12%',
            'sidebar-accent-foreground': '350 30% 96%',
            'sidebar-border': '350 15% 18%',
            'sidebar-primary': '347 77% 50%',
            'sidebar-primary-foreground': '350 40% 98%',
        },
    },
];

interface ThemeContextType {
    theme: Theme;
    mode: 'light' | 'dark';
    setTheme: (name: string) => void;
    setMode: (mode: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextType>({
    theme: themes[0],
    mode: 'light',
    setTheme: () => {},
    setMode: () => {},
});

function applyTheme(theme: Theme, mode: 'light' | 'dark') {
    const root = document.documentElement;
    const colors = mode === 'dark' ? theme.dark : theme.colors;

    Object.entries(colors).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
    });

    if (mode === 'dark') {
        root.classList.add('dark');
    } else {
        root.classList.remove('dark');
    }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [themeName, setThemeName] = useState(() => {
        return localStorage.getItem('photostat-theme') || 'slate';
    });
    const [mode, setModeState] = useState<'light' | 'dark'>(() => {
        return (localStorage.getItem('photostat-mode') as 'light' | 'dark') || 'light';
    });

    const theme = themes.find((t) => t.name === themeName) || themes[0];

    const setTheme = useCallback((name: string) => {
        setThemeName(name);
        localStorage.setItem('photostat-theme', name);
    }, []);

    const setMode = useCallback((m: 'light' | 'dark') => {
        setModeState(m);
        localStorage.setItem('photostat-mode', m);
    }, []);

    useEffect(() => {
        applyTheme(theme, mode);
    }, [theme, mode]);

    return (
        <ThemeContext.Provider value={{ theme, mode, setTheme, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
